import { Box } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { ContentHeader } from './components/ContentHeader';
import { Tabs } from './components/Tabs';
import { ContentWithSidebar } from '../../../../components/ContentWithSidebar';
import { FeedTab } from './tabs/FeedTab';
import { PhotosTab } from './tabs/PhotosTab';
import { ActivityTab } from './tabs/ActivityTab';
import { RankingTab } from './tabs/RankingTab';
import { SessionsTab } from './tabs/SessionsTab';
import { photos } from './helpers';
import { buildCompetitionDetailsTabIndices } from './tabIndices';
import { SubmissionSidebarPanel } from './components/SubmissionSidebarPanel';
import { RegisterModal } from './components/RegisterModal';
import { WithdrawFromCompetitionDialog } from '../dialogs/WithdrawFromCompetitionDialog';
import { SubmitScoreSidebarPanel } from './components/SubmitScoreSidebarPanel';
import { ManagersSidebarPanel } from './components/ManagersSidebarPanel';
import { SessionsSidebarPanel } from './components/SessionsSidebarPanel';
import { SessionRankingDialog } from './components/SessionRankingDialog';
import { YourRankingSidebarPanel } from './components/YourRankingSidebarPanel';
import { useAuth } from '@context/AuthProvider';
import { YourScoresTab } from './tabs/YourScoresTab';
import {
  Advertisement,
  RankList,
} from '../../../../components/content-side-bar';
import { BackLink } from '@components/BackLink';
import { ROUTES } from '@routes/routes';
import { useParams, useSearchParams } from 'react-router';
import {
  useEnrollInCompetitionMutation,
  useWithdrawFromCompetitionMutation,
  useGetPortalCompetition,
  useGetPortalCompletedCompetition,
  useGetPortalCompetitionEnrollment,
  useGetPortalCompetitionRanking,
  useGetPortalCompletedCompetitionRanking,
  useGetPortalCompetitionSessions,
} from '@reactQuery/competitions';
import {
  getCompetitionStatusName,
  getCompetitionFormatName,
  mapPortalCompetitionToCard,
  mapPortalRankingEntryToRank,
} from '../helpers';
import { memberHasRequiredShootingClearance } from '@helpers/shootingEnrollment';
import { Loader } from '@components/feed/Loader';
import { EmptyPlaceholder } from '@components/EmptyPlaceholder';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { PortalCompetitionSession } from '@api/competitionsApi/interfaces';
import { ICompetitionDetails } from './interfaces';
import { AxiosError } from 'axios';
import { ValidationErrors } from '@api/interfaces';
import { usePortalWeaponGroupCategoryMap } from '@reactQuery/shooting';
import { useInDevMode } from '@hooks/useInDevMode';

export const CompetitionDetails: React.FC<ICompetitionDetails> = ({
  variant = 'current',
}): JSX.Element => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { id: slug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [registerModalOpen, setRegisterModalOpen] = useState<boolean>(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState<boolean>(false);
  const [sessionRankingDialogSession, setSessionRankingDialogSession] =
    useState<PortalCompetitionSession | null>(null);
  const isResults = variant === 'results';
  const inDevMode = useInDevMode();
  const showPhotosTab = inDevMode;
  const showActivityTab = inDevMode;

  const { data: currentData, isLoading: isLoadingCurrent } =
    useGetPortalCompetition(slug, !isResults);
  const { data: completedData, isLoading: isLoadingCompleted } =
    useGetPortalCompletedCompetition(slug, isResults);
  const { data: enrollment, isLoading: isLoadingEnrollment } =
    useGetPortalCompetitionEnrollment(slug);
  const { data: currentRanking, isLoading: isLoadingCurrentRanking } =
    useGetPortalCompetitionRanking(slug, !isResults);
  const { data: completedRanking, isLoading: isLoadingCompletedRanking } =
    useGetPortalCompletedCompetitionRanking(slug, isResults);
  const { mutate: enrollInCompetition, isPending: isEnrolling } =
    useEnrollInCompetitionMutation();
  const { mutate: withdrawFromCompetition, isPending: isWithdrawing } =
    useWithdrawFromCompetitionMutation();

  const data = isResults ? completedData : currentData;
  const isLoading = isResults ? isLoadingCompleted : isLoadingCurrent;
  const isSessionFormat = data?.format === 'session';
  const { data: sessions = [], isLoading: isLoadingSessions } =
    useGetPortalCompetitionSessions(
      slug,
      isResults ? 'results' : 'current',
      Boolean(slug) && isSessionFormat && !isLoading
    );
  const isLoadingRanking = isResults
    ? isLoadingCompletedRanking
    : isLoadingCurrentRanking;
  const ranking = isResults ? completedRanking : currentRanking;
  const backRoute = isResults
    ? ROUTES.portal.competitions.results.overview
    : ROUTES.portal.competitions.overview;
  const backLabel = isResults
    ? t('competitions:portal.results.details.backLink')
    : t('competitions:portal.details.backLink');

  const competition = useMemo(
    () => (data ? mapPortalCompetitionToCard(data) : undefined),
    [data]
  );

  const rankingItems = useMemo(() => ranking?.data ?? [], [ranking]);

  const topRanking = useMemo(
    () =>
      rankingItems
        .filter((entry) => entry.rank !== null)
        .slice(0, 3)
        .map(mapPortalRankingEntryToRank),
    [rankingItems]
  );

  const myRankingEntry = useMemo(() => {
    const memberSlug = currentUser?.member?.slug;

    if (!memberSlug) {
      return undefined;
    }

    return rankingItems.find((entry) => entry.member.slug === memberSlug);
  }, [currentUser?.member?.slug, rankingItems]);

  const { categoryMap } = usePortalWeaponGroupCategoryMap(Boolean(slug));
  const isRegistered = enrollment?.enrolled === true;
  const requiredWeaponGroups = data?.discipline.weaponGroups ?? [];
  const canRegisterByWeaponGroup =
    categoryMap !== undefined
      ? memberHasRequiredShootingClearance(
          requiredWeaponGroups,
          currentUser?.member?.shootingClearances,
          categoryMap
        )
      : true;
  const registerBlockedByWeaponGroup =
    !isRegistered &&
    requiredWeaponGroups.length > 0 &&
    !canRegisterByWeaponGroup;
  const showScoreSubmissions =
    isRegistered && data?.format === 'self_paced';
  const tabIndices = useMemo(
    () =>
      buildCompetitionDetailsTabIndices({
        showSessionsTab: isSessionFormat,
        showScoreSubmissions,
        showPhotosTab,
        showActivityTab,
      }),
    [isSessionFormat, showScoreSubmissions, showPhotosTab, showActivityTab]
  );

  useEffect(() => {
    if (searchParams.get('tab') !== 'sessions') {
      return;
    }

    if (tabIndices.sessions === null) {
      return;
    }

    setCurrentTab(tabIndices.sessions);
  }, [searchParams, tabIndices]);

  const showRegisterModal = (): void => {
    setRegisterModalOpen(true);
  };

  const hideRegisterModal = (): void => {
    setRegisterModalOpen(false);
  };

  const handleRegister = (): void => {
    enrollInCompetition(slug, {
      onSuccess: () => {
        hideRegisterModal();
        enqueueSnackbar(t('competitions:portal.dialogs.register.success'), {
          variant: 'success',
        });
      },
      onError: (error: AxiosError<ValidationErrors>) => {
        const fieldMessage = error.response?.data?.errors?.weaponGroup?.[0];

        enqueueSnackbar(
          fieldMessage ?? t('competitions:portal.dialogs.register.failed'),
          {
            variant: 'error',
          }
        );
      },
    });
  };

  const showWithdrawModal = (): void => {
    setWithdrawModalOpen(true);
  };

  const hideWithdrawModal = (): void => {
    setWithdrawModalOpen(false);
  };

  const handleWithdraw = (reason: string): void => {
    withdrawFromCompetition(
      {
        slug,
        reason: reason.trim() === '' ? undefined : reason.trim(),
      },
      {
        onSuccess: () => {
          hideWithdrawModal();
          enqueueSnackbar(t('competitions:portal.dialogs.withdraw.success'), {
            variant: 'success',
          });
        },
        onError: () => {
          enqueueSnackbar(t('competitions:portal.dialogs.withdraw.failed'), {
            variant: 'error',
          });
        },
      }
    );
  };

  if (isLoading || isLoadingEnrollment || isLoadingRanking) {
    return (
      <Box>
        <BackLink
          route={backRoute}
          sx={{
            display: 'inline-block',
            mb: '10px',
          }}
        >
          {backLabel}
        </BackLink>
        <Box py={8}>
          <Loader />
        </Box>
      </Box>
    );
  }

  if (!competition || !data) {
    return (
      <Box>
        <BackLink
          route={backRoute}
          sx={{
            display: 'inline-block',
            mb: '10px',
          }}
        >
          {backLabel}
        </BackLink>
        <EmptyPlaceholder
          icon={EmojiEventsIcon}
          title={t(
            isResults
              ? 'competitions:portal.results.details.emptyPlaceholders.notFound.title'
              : 'competitions:portal.details.emptyPlaceholders.notFound.title'
          )}
          subTitle={t(
            isResults
              ? 'competitions:portal.results.details.emptyPlaceholders.notFound.subTitle'
              : 'competitions:portal.details.emptyPlaceholders.notFound.subTitle'
          )}
        />
      </Box>
    );
  }

  const canSubmitScores = !isResults && showScoreSubmissions;
  const sessionsTabIndex = tabIndices.sessions;

  const handleShowAllSessions = (): void => {
    if (sessionsTabIndex === null) {
      return;
    }

    setCurrentTab(sessionsTabIndex);
  };

  const handleCompletedSessionClick = (
    session: PortalCompetitionSession
  ): void => {
    setSessionRankingDialogSession(session);
  };

  const handleCloseSessionRankingDialog = (): void => {
    setSessionRankingDialogSession(null);
  };

  const SideBar: JSX.Element = (
    <Fragment>
      <SubmissionSidebarPanel
        status={isRegistered ? 'registered' : 'not-registered'}
        readOnly={isResults}
        onRegisterBtnClick={isResults ? undefined : showRegisterModal}
        onWithdrawBtnClick={isResults ? undefined : showWithdrawModal}
        description={competition.description}
        discipline={competition.discipline}
        weaponGroups={competition.weaponGroups}
        startDate={competition.startDate}
        endDate={competition.endDate}
        statusName={getCompetitionStatusName(competition.status)}
        formatName={getCompetitionFormatName(competition.format)}
        participants={data.enrollmentCount}
        registerBlockedByWeaponGroup={registerBlockedByWeaponGroup}
      />
      {isRegistered && currentTab !== tabIndices.ranking && (
        <YourRankingSidebarPanel
          rankingEntry={myRankingEntry}
          participantCount={data.enrollmentCount}
          minCountingScores={ranking?.meta.minCountingScores}
          startDate={competition.startDate}
          endDate={competition.endDate}
          onViewRanking={() => setCurrentTab(tabIndices.ranking)}
        />
      )}
      {canSubmitScores && tabIndices.yourScores !== null && (
        <SubmitScoreSidebarPanel
          slug={competition.slug}
          startsOn={data.startsOn}
          endsOn={data.endsOn}
          weaponGroups={data.discipline.weaponGroups ?? []}
          onSubmit={() => {
            setCurrentTab(tabIndices.yourScores as number);
          }}
        />
      )}
      {isSessionFormat && (
        <SessionsSidebarPanel
          sessions={sessions}
          isLoading={isLoadingSessions}
          onShowMore={handleShowAllSessions}
          onCompletedSessionClick={handleCompletedSessionClick}
        />
      )}
      <ManagersSidebarPanel managers={data.managers} />
      {currentTab !== tabIndices.ranking && topRanking.length > 0 && (
        <RankList ranking={topRanking} />
      )}
      <Advertisement />
    </Fragment>
  );

  const wrapContent = ((): boolean => {
    if (
      tabIndices.yourScores !== null &&
      currentTab === tabIndices.yourScores
    ) {
      return false;
    }

    return currentTab > tabIndices.ranking;
  })();

  return (
    <Fragment>
      <Box>
        <BackLink
          route={backRoute}
          sx={{
            display: 'inline-block',
            mb: '10px',
          }}
        >
          {backLabel}
        </BackLink>

        <ContentHeader
          title={competition.title}
          backgroundSrc={competition.imgSrc}
          manageHref={
            data.canManage
              ? ROUTES.manage.competitions.details(competition.slug)
              : undefined
          }
          podium={isResults ? rankingItems.slice(0, 3) : undefined}
          enrollmentCount={isResults ? data.enrollmentCount : undefined}
        >
          <Tabs
            currentTab={currentTab}
            onChange={setCurrentTab}
            showScoreSubmissions={showScoreSubmissions}
            showSessionsTab={isSessionFormat}
            showPhotosTab={showPhotosTab}
            showActivityTab={showActivityTab}
          />
        </ContentHeader>
        <ContentWithSidebar
          sidebarContent={SideBar}
          wrapContentInPanel={wrapContent}
        >
          <FeedTab show={currentTab === tabIndices.feed} slug={competition.slug} />
          <RankingTab
            show={currentTab === tabIndices.ranking}
            slug={competition.slug}
            format={data.format}
            variant={isResults ? 'results' : 'current'}
          />
          {sessionsTabIndex !== null && (
            <SessionsTab
              show={currentTab === sessionsTabIndex}
              sessions={sessions}
              isLoading={isLoadingSessions}
              onCompletedSessionClick={handleCompletedSessionClick}
            />
          )}
          {tabIndices.photos !== null && (
            <PhotosTab
              show={currentTab === tabIndices.photos}
              photos={photos}
            />
          )}
          {showScoreSubmissions &&
            tabIndices.yourScores !== null &&
            currentTab === tabIndices.yourScores && (
              <YourScoresTab slug={competition.slug} />
            )}
          {tabIndices.activity !== null && (
            <ActivityTab show={currentTab === tabIndices.activity} />
          )}
        </ContentWithSidebar>
      </Box>

      {!isResults && (
        <Fragment>
          <RegisterModal
            show={registerModalOpen}
            title={data.title}
            description={data.description}
            isPending={isEnrolling}
            onClose={hideRegisterModal}
            onRegister={handleRegister}
          />
          <WithdrawFromCompetitionDialog
            show={withdrawModalOpen}
            slug={slug}
            competitionTitle={data.title}
            isPending={isWithdrawing}
            onClose={hideWithdrawModal}
            onConfirm={handleWithdraw}
          />
        </Fragment>
      )}

      <SessionRankingDialog
        show={sessionRankingDialogSession !== null}
        slug={slug}
        session={sessionRankingDialogSession}
        variant={isResults ? 'results' : 'current'}
        onClose={handleCloseSessionRankingDialog}
      />
    </Fragment>
  );
};
